import { fetchApi, trackError } from '@vend/utilities'
import type { PaymentMethod } from '@vend/business-components/dist/components/LspayCnp/queries'
import { ApiTransaction } from '@vend/types'
import { LSPAY_PRODUCT_VERSION } from '../../components/payments/lspay-payment-modal/helpers/lspayConstants'
import {
  SwiperError,
  SwiperErrorResponse,
} from '../../components/payments/lspay-payment-modal/helpers/lspay.types'

/**
 * Fetch saved payment methods for a customer from LSPay
 * @param customerId - The customer ID
 * @param outletId - The outlet ID
 * @returns Promise<PaymentMethod[]> - Array of saved payment methods
 */
export const fetchLSPayCustomerPaymentMethods = async (
  customerId: string,
  outletId: string
): Promise<PaymentMethod[]> => {
  try {
    const response = await fetchApi(
      `/api/2.0/lspay/customers/${customerId}/payment-methods?outlet=${outletId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )

    const data = await response.json()

    // Filter payment methods: only show cards that can be processed at location and are not expired
    const filteredPaymentMethods = (data?.payment_methods ?? []).filter(
      (method: PaymentMethod) => {
        const canProcess = method.can_process_at_location === true
        const notExpired = method.details?.expired !== true
        return canProcess && notExpired
      }
    )

    return filteredPaymentMethods
  } catch (error) {
    trackError('[LSPayPMOF] Failed to fetch customer payment methods', {
      data: {
        error,
        customerId,
        outletId,
      },
    })

    // Return an empty array on error to prevent UI crashes
    return []
  }
}

/**
 * Process a payment using a customer's saved payment method
 * @param amount - The amount to charge in the smallest currency unit (e.g., cents)
 * @param customerId - The customer ID
 * @param paymentMethodId - The saved payment method ID to charge
 * @param registerId - The register ID
 * @param registerName - The register name
 * @param registerSaleId - The sale ID
 * @param registerSalePaymentId - The payment ID to associate with the payment
 * @returns The parsed response from the API
 */
export const processLSPayPaymentMethodOnFile = async (
  amount: number,
  customerId: string,
  paymentMethodId: string,
  registerId: string,
  registerName: string,
  registerSaleId: string,
  registerSalePaymentId: string
): Promise<ApiTransaction> => {
  try {
    const response = await fetchApi(
      `/api/2.0/lspay/customers/${customerId}/payment-methods/process`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount,
          idempotency_key: registerSalePaymentId,
          metadata: {
            deviceId: registerId,
            deviceName: registerName,
            hrOrderId: registerSaleId,
            hrPaymentId: registerSalePaymentId,
            salesChannel: 'IN_STORE',
            productVersion: LSPAY_PRODUCT_VERSION,
          },
          payment_method_id: paymentMethodId,
          register_id: registerId,
          register_sale_payment_id: registerSalePaymentId,
        }),
      }
    )

    if (!response.ok) {
      const swiperError: SwiperErrorResponse = await response.json()
      throw new SwiperError(
        swiperError.error,
        swiperError.code,
        swiperError.fields
      )
    }

    return response.json()
  } catch (error) {
    trackError('[LSPayPMOF] Failed to process customer payment method', {
      data: {
        error,
        customerId,
        paymentMethodId,
        amount,
      },
    })
    throw error
  }
}
